import React, { useState, useEffect, useRef, Suspense } from "react";
import { Layout } from "./components/Layout";
import {
  AuthPage,
  AuthVerifyPage,
  SignupPage,
  DashboardPage,
  TrainingPage,
} from "./pages";

// Lazy-loaded pages (code splitting)
const AdminPage = React.lazy(() => import("./pages/AdminPage").then(m => ({ default: m.AdminPage })));
const AdminCoachingBriefsPage = React.lazy(() => import("./pages/AdminCoachingBriefsPage").then(m => ({ default: m.AdminCoachingBriefsPage })));
const AdminEvaluationCriteriaMatrixPage = React.lazy(() => import("./pages/AdminEvaluationCriteriaMatrixPage").then(m => ({ default: m.AdminEvaluationCriteriaMatrixPage })));
const AdminPersonalityStylesPage = React.lazy(() => import("./pages/AdminPersonalityStylesPage").then(m => ({ default: m.AdminPersonalityStylesPage })));
const AdminDrillPage = React.lazy(() => import("./pages/AdminDrillPage").then(m => ({ default: m.AdminDrillPage })));
const AdminBanqueDeCasPage = React.lazy(() => import("./pages/AdminBanqueDeCasPage").then(m => ({ default: m.AdminBanqueDeCasPage })));
const DrillPage = React.lazy(() => import("./pages/DrillPage").then(m => ({ default: m.DrillPage })));
const AdminKestioKnowledgePage = React.lazy(() => import("./pages/AdminKestioKnowledgePage").then(m => ({ default: m.AdminKestioKnowledgePage })));
const AdminLLMRoutesPage = React.lazy(() => import("./pages/AdminLLMRoutesPage").then(m => ({ default: m.AdminLLMRoutesPage })));
const AccountPage = React.lazy(() => import("./pages/AccountPage").then(m => ({ default: m.AccountPage })));
const BuyPackPage = React.lazy(() => import("./pages/BuyPackPage").then(m => ({ default: m.BuyPackPage })));
const PreviewRateLimitPage = React.lazy(() => import("./pages/PreviewRateLimitPage").then(m => ({ default: m.PreviewRateLimitPage })));
const CostDashboardPage = React.lazy(() => import("./pages/CostDashboardPage").then(m => ({ default: m.CostDashboardPage })));
const UsageAnalyticsPage = React.lazy(() => import("./pages/UsageAnalyticsPage").then(m => ({ default: m.UsageAnalyticsPage })));
const AdminTechDocPage = React.lazy(() => import("./pages/AdminTechDocPage").then(m => ({ default: m.AdminTechDocPage })));
const ChallengePage = React.lazy(() => import("./pages/ChallengePage").then(m => ({ default: m.ChallengePage })));
const CertificationPathsPage = React.lazy(() => import("./pages/CertificationPathsPage").then(m => ({ default: m.CertificationPathsPage })));
const MyProgressPage = React.lazy(() => import("./pages/MyProgressPage").then(m => ({ default: m.MyProgressPage })));
const MEntrainerPage = React.lazy(() => import("./pages/MEntrainerPage").then(m => ({ default: m.MEntrainerPage })));
const RewardsPage = React.lazy(() => import("./pages/RewardsPage").then(m => ({ default: m.RewardsPage })));
const AdminRelanceRulesPage = React.lazy(() => import("./pages/AdminRelanceRulesPage").then(m => ({ default: m.AdminRelanceRulesPage })));
const AdminReusabilityKpisPage = React.lazy(() => import("./pages/AdminReusabilityKpisPage").then(m => ({ default: m.AdminReusabilityKpisPage })));
const AdminPathTemplatesPage = React.lazy(() => import("./pages/AdminPathTemplatesPage").then(m => ({ default: m.AdminPathTemplatesPage })));
const AdminProgramRulesPage = React.lazy(() => import("./pages/AdminProgramRulesPage").then(m => ({ default: m.AdminProgramRulesPage })));
const AdminIntentContextsPage = React.lazy(() => import("./pages/AdminIntentContextsPage").then(m => ({ default: m.AdminIntentContextsPage })));
const AdminPortfoliosPage = React.lazy(() => import("./pages/AdminPortfoliosPage").then(m => ({ default: m.AdminPortfoliosPage })));
const AdminLegalDocumentsPage = React.lazy(() => import("./pages/AdminLegalDocumentsPage").then(m => ({ default: m.AdminLegalDocumentsPage })));
const CertificatePage = React.lazy(() => import("./pages/CertificatePage").then(m => ({ default: m.CertificatePage })));
const HistoryPage = React.lazy(() => import("./pages/HistoryPage").then(m => ({ default: m.HistoryPage })));
const HelpPage = React.lazy(() => import("./pages/HelpPage").then(m => ({ default: m.HelpPage })));
const AgentPresentationPage = React.lazy(() => import("./pages/AgentPresentationPage").then(m => ({ default: m.AgentPresentationPage })));
const CoachPage = React.lazy(() => import("./pages/CoachPage").then(m => ({ default: m.CoachPage })));
const OnboardingPage = React.lazy(() => import("./pages/OnboardingPage").then(m => ({ default: m.OnboardingPage })));
const ReportingTab = React.lazy(() => import("./components/ReportingTab").then(m => ({ default: m.ReportingTab })));
const NotificationsPage = React.lazy(() => import("./pages/NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const NotificationPreferencesPage = React.lazy(() => import("./pages/NotificationPreferencesPage").then(m => ({ default: m.NotificationPreferencesPage })));
const CompanyNotificationSettingsPage = React.lazy(() => import("./pages/CompanyNotificationSettingsPage").then(m => ({ default: m.CompanyNotificationSettingsPage })));
// Sales Coach IA Réel (chantier 2.1)
const SessionsLivePage = React.lazy(() => import("./pages/SessionsLivePage").then(m => ({ default: m.SessionsLivePage })));
const SessionsTrainingPage = React.lazy(() => import("./pages/SessionsTrainingPage").then(m => ({ default: m.SessionsTrainingPage })));
const EncadrantPage = React.lazy(() => import("./pages/EncadrantPage").then(m => ({ default: m.EncadrantPage })));
/** Même clé que `TEAM_HISTORY_OPEN_KEY` dans EncadrantPage (module chargé à la demande). */
const TEAM_HISTORY_OPEN_KEY = "bizcoach_team_history_open";
const openTeamHistory = () => { try { localStorage.setItem(TEAM_HISTORY_OPEN_KEY, "1"); } catch { /* noop */ } };
const SalesCoachNewPage = React.lazy(() => import("./pages/SalesCoachNewPage").then(m => ({ default: m.SalesCoachNewPage })));
const RecordMeetingModal = React.lazy(() => import("./components/RecordMeetingModal").then(m => ({ default: m.RecordMeetingModal })));
const SalesCoachDetailPage = React.lazy(() => import("./pages/SalesCoachDetailPage").then(m => ({ default: m.SalesCoachDetailPage })));
const SalesCoachLibraryPage = React.lazy(() => import("./pages/SalesCoachLibraryPage").then(m => ({ default: m.SalesCoachLibraryPage })));
const SalesCoachSharePage = React.lazy(() => import("./pages/SalesCoachSharePage").then(m => ({ default: m.SalesCoachSharePage })));
const WaveformGalleryPage = React.lazy(() => import("./pages/WaveformGalleryPage").then(m => ({ default: m.WaveformGalleryPage })));
import type { User, AIAgent, PageType, Feedback, ActiveChallengeInfo } from "./types";
import { authApi, accountApi, challengesApi, clearToken, legalDocumentsApi } from "./lib/api";
import { LegalAcceptanceGate, type PendingDpa } from "./components/LegalAcceptanceGate";
import { Loader2 } from "lucide-react";
import { detectCoachKind } from "./lib/coach-kind";
import "./index.css";
import "./i18n";
import { changeLanguage } from "./i18n";
import { useUrlSync } from "./hooks/useUrlSync";
import { encadrantRoleFor, canOpenTeamReporting, type EncadrantBloc } from "./lib/encadrant";
import { useTranslation } from "react-i18next";
import { notifySeancesChanged } from "./components/plan/MySeances";
import { drillOuvertPour, setDrillVisible } from "./lib/drill-visibility";
import { setSidebandVisible } from "./lib/sideband-visibility";
import { useDrillVisible } from "./hooks/useDrillVisible";

/**
 * Vrai uniquement sur les environnements de dev (localhost ou instances
 * Scalingo `*.osc-fr1.scalingo.io` = bizcoach-dev / bizcoach-dev-fabien).
 * Faux sur staging/demo/prod (domaines `*.kestio.live`). Sert à réserver les
 * outils de design internes (ex. la galerie /waveforms) au dev.
 */
function isDevHost(): boolean {
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h.endsWith(".osc-fr1.scalingo.io");
}

function getMagicLinkToken(): string | null {
  const url = new URL(window.location.href);
  if (url.pathname === "/auth/verify") {
    return url.searchParams.get("token");
  }
  return null;
}

export default function App() {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState<PageType>("dashboard");
  // Espace encadrant (« Deux casquettes ») : bloc actif quand currentPage === "encadrant".
  const [encadrantBloc, setEncadrantBloc] = useState<EncadrantBloc>("piloter");
  // Sous-écran ciblé dans ce bloc. `undefined` = le premier de la liste.
  // Remonté ici (et non local à EncadrantPage) pour que le cockpit puisse
  // envoyer vers « Concevoir › Parcours » depuis un bouton d'alerte.
  const [encadrantSub, setEncadrantSub] = useState<string | undefined>(undefined);
  /** Onglet de « Mon compte » sur lequel arriver (posé par une alerte du cockpit). */
  const [accountTab, setAccountTab] = useState<string | undefined>(undefined);
  const [selectedAgent, setSelectedAgent] = useState<AIAgent | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  // Onglet HistoryPage à ouvrir au prochain affichage (utilisé par SalesCoachPage
  // pour permettre la navigation retour vers les autres onglets de l'historique)
  const [historyInitialTab, setHistoryInitialTab] = useState<"training" | "prep-meeting" | null>(null);
  // Reprise d'une fiche Prep Meeting depuis l'historique (sessionId existant)
  const [prepMeetingResumeSessionId, setPrepMeetingResumeSessionId] = useState<string | null>(null);
  // Page à afficher au retour du CoachPage. null = dashboard par défaut (chemin depuis
  // l'accueil). "history" quand on a ouvert la fiche depuis l'historique Coach Prepa —
  // évite de renvoyer l'user au dashboard alors qu'il vient de son historique.
  const [coachBackPage, setCoachBackPage] = useState<PageType | null>(null);
  const [trainingActive, setTrainingActive] = useState(false);
  const [magicToken, setMagicToken] = useState<string | null>(getMagicLinkToken);
  const [adminTargetAccountId, setAdminTargetAccountId] = useState<string | null>(null);
  const [selectedChallengeId, setSelectedChallengeId] = useState<string | null>(null);
  const [challengeBackPage, setChallengeBackPage] = useState<PageType>("challenge");
  const [challengeContextId, setChallengeContextId] = useState<string | null>(null);
  const [activeChallenges, setActiveChallenges] = useState<ActiveChallengeInfo[]>([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  // DPA à accepter par l'ORG avant d'utiliser l'application (plan feat-acceptation-cgv-dpa, lot 5).
  // `undefined` = pas encore vérifié, `null` = rien à accepter.
  const [legalPending, setLegalPending] = useState<PendingDpa | null | undefined>(undefined);
  const [salesCoachSessionId, setSalesCoachSessionId] = useState<string | null>(null);
  const [recordModalOpen, setRecordModalOpen] = useState(false);

  // URL sync — synchronise currentPage ↔ URL du navigateur
  useUrlSync({
    currentPage, selectedChallengeId, adminTargetAccountId,
    isAuthenticated,
    setCurrentPage, setSelectedChallengeId, setAdminTargetAccountId,
    setChallengeBackPage,
    // Rend chaque sous-écran encadrant adressable : un rechargement gardait
    // l'espace mais retombait sur le cockpit, et aucun lien vers « Agir ›
    // Campagnes » n'était partageable.
    encadrantBloc, encadrantSub,
    setEncadrantBloc: (b: string) => setEncadrantBloc(b as EncadrantBloc),
    setEncadrantSub,
    enabled: true, // kill switch : mettre false pour désactiver
  });

  // « S'exercer » et feedback en direct (sideband) : interrupteurs servis par /api/config
  // (S'exercer : combiné au profil, l'ADM le voit toujours).
  // Fermé tant que la réponse n'est pas arrivée ou en cas d'erreur.
  useEffect(() => {
    if (!user) { setDrillVisible(false); setSidebandVisible(false); return; }
    let actif = true;
    fetch("/api/config").then((r) => r.json())
      .then((cfg: { drillEnabled?: boolean; sidebandEnabled?: boolean }) => {
        if (!actif) return;
        setDrillVisible(drillOuvertPour(cfg.drillEnabled === true, user.profileType));
        // Feedback en direct pendant l'appel (sideband) : interrupteur seul, ADM compris.
        setSidebandVisible(cfg.sidebandEnabled === true);
      })
      .catch(() => { if (actif) { setDrillVisible(false); setSidebandVisible(false); } });
    return () => { actif = false; };
  }, [user?.id, user?.profileType]);
  const drillVisible = useDrillVisible();

  // Auto-login via JWT au chargement
  useEffect(() => {
    // If there's a magic link token, skip auto-login — let AuthVerifyPage handle it
    if (magicToken) {
      setIsLoading(false);
      return;
    }

    // Skip auth check on public pages (signup, auth) to avoid 401 in console
    if (window.location.pathname.startsWith("/signup") || window.location.pathname === "/auth") {
      setIsLoading(false);
      return;
    }

    // Call /api/auth/me — httpOnly cookie is invisible to JS
    authApi
      .me()
      .then((u) => {
        setUser(u);
        setIsAuthenticated(true);
        if (u.language) changeLanguage(u.language);

        // Handle deep link from email: ?page=challenge&challengeId=xxx
        const params = new URLSearchParams(window.location.search);
        const pageParam = params.get("page");
        const challengeIdParam = params.get("challengeId");
        if (pageParam === "challenge" && challengeIdParam) {
          setSelectedChallengeId(challengeIdParam);
          setCurrentPage("challenge");
          window.history.replaceState({}, "", "/");
        }
      })
      .catch(() => {
        clearToken();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Atterrissage par défaut sur l'espace Pilot (encadrant) pour un manager / un
  // organisateur — ils pilotent d'abord. Une seule fois au chargement, et seulement
  // sur la racine (un deep link vers une page précise reste respecté). Déclaré APRÈS
  // useUrlSync pour gagner sur la synchro d'URL. L'utilisateur peut basculer sur Play.
  const encadrantDefaulted = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !user || encadrantDefaulted.current) return;
    encadrantDefaulted.current = true;
    const p = window.location.pathname;
    const onRoot = p === "/" || p === "/dashboard";
    if (onRoot && (user.profileType === "MNG" || user.profileType === "ORG")) {
      setCurrentPage("encadrant");
    }
  }, [isAuthenticated, user]);

  // Acceptation du DPA (lot 5, décision D1 = C) : seul l'ORG est concerné ; le serveur répond
  // « rien à accepter » pendant un « Voir comme ». En cas d'échec de la vérification, on n'enferme
  // pas le client : l'écran reviendra à la prochaine connexion.
  const checkLegalPending = () => {
    legalDocumentsApi.pending()
      .then((r) => setLegalPending(r.pending))
      .catch((err) => { console.error("[legal] vérification du DPA impossible:", err); setLegalPending(null); });
  };
  useEffect(() => {
    if (!isAuthenticated || !user) { setLegalPending(undefined); return; }
    if (user.profileType !== "ORG") { setLegalPending(null); return; }
    if (legalPending === undefined) checkLegalPending();
  }, [isAuthenticated, user]);

  // Onboarding requis : ORG dont l'espace n'a pas encore été configuré
  // (client_accounts.onboarding_completed_at null, exposé via /api/auth/me).
  // Le wizard ne s'affiche QUE pour le profil ORG et UNIQUEMENT à l'activation
  // du compte (1ʳᵉ fois) : le flag DB `onboarding_completed_at` est la seule
  // source de vérité. Dès que l'ORG termine OU passe (skip) l'onboarding, le
  // flag est posé en base et le wizard ne se re-déclenche plus aux connexions
  // suivantes — la checklist d'accueil (DashboardPage) prend alors le relais
  // pour les étapes encore non réalisées (description entreprise / 1er cas).
  // Remplace l'ancienne heuristique « 0 offre ET 0 persona » + l'ancien garde
  // sessionStorage (qui ne survivait pas à une reconnexion → le wizard
  // redémarrait à chaque login ORG).
  useEffect(() => {
    if (!isAuthenticated || !user || onboardingChecked) return;
    if (!["ORG"].includes(user.profileType)) { setOnboardingChecked(true); return; }
    // On affiche l'onboarding UNIQUEMENT si l'espace n'est ni marqué onboardé
    // (flag DB), ni DÉJÀ configuré de fait. `accountConfigured` (entreprise décrite
    // + au moins un cas, exposé par /api/auth/me) évite de re-proposer l'onboarding
    // à un ORG dont le compte a été rempli autrement que par le tunnel (flag resté
    // NULL) — cas du compte pré-configuré / repris.
    if (!(user as any).accountOnboardingCompletedAt && !(user as any).accountConfigured) {
      setShowOnboarding(true);
    }
    setOnboardingChecked(true);
  }, [isAuthenticated, user, onboardingChecked]);

  // Marque l'onboarding comme terminé côté serveur (idempotent) ET met à jour
  // l'objet user local pour que le wizard ne se re-déclenche pas dans la même
  // session. Appelé aussi bien à la complétion qu'au skip : dans les deux cas
  // l'ORG a « vu » l'activation, donc on ne la repropose plus.
  const markOnboardingDone = () => {
    // Non bloquant pour la session en cours (l'état local suffit), mais sans
    // enregistrement l'onboarding réapparaîtra à la reconnexion, sans
    // explication pour l'utilisateur.
    accountApi.completeOnboarding().catch((err) => {
      console.error("[onboarding] fin non enregistrée:", err);
    });
    setUser((prev) => (prev ? ({ ...prev, accountOnboardingCompletedAt: new Date().toISOString() } as User) : prev));
    setShowOnboarding(false);
    setCurrentPage("dashboard");
  };

  const handleLogin = (loggedUser: User) => {
    setUser(loggedUser);
    setIsAuthenticated(true);
    // MNG/ORG → espace Pilot par défaut (cf. effet ci-dessus) ; on marque le ref
    // pour éviter un double positionnement, et on route directement.
    encadrantDefaulted.current = true;
    setCurrentPage(loggedUser.profileType === "MNG" || loggedUser.profileType === "ORG" ? "encadrant" : "dashboard");
    setMagicToken(null);
    setOnboardingChecked(false); // Trigger onboarding check
    if (loggedUser.language) changeLanguage(loggedUser.language);
    // Clean URL after magic link verification
    window.history.replaceState({}, "", "/");
  };

  const handleLogout = () => {
    authApi.logout().catch(() => {});
    clearToken();
    setUser(null);
    setIsAuthenticated(false);
    setCurrentPage("auth");
  };

  // Couture Sales Coach Réel → entraînement (Pilier 1 ↔ Pilier 2).
  // Lance le simulateur correspondant à une faiblesse identifiée dans le débrief.
  // simulatorKey vient de la reco Claude : PROSPECTOR | DISCOVERY | PITCH | NEGOTIATOR | SATISFACTOR | MEDDIC.
  const handleStartTrainingFromCoach = async (simulatorKey: string) => {
    // Mapping clé Claude → nom du simulateur en BDD
    const KEY_TO_SIM_NAME: Record<string, string[]> = {
      PROSPECTOR: ["Prospector"],
      DISCOVERY: ["Discovery Call", "Discovery"],
      PITCH: ["Pitch", "Elevator Pitch"],
      NEGOTIATOR: ["Negotiator"],
      SATISFACTOR: ["Satisfactor", "Customer Success"],
      MEDDIC: ["MEDDIC"],
    };
    try {
      const agents = await authApi.getAgents();
      const candidates = KEY_TO_SIM_NAME[simulatorKey] ?? [simulatorKey];
      const agent = agents.find((a: AIAgent) =>
        candidates.some((c) => a.name?.toLowerCase() === c.toLowerCase()),
      );
      if (!agent) {
        alert(`Simulateur "${simulatorKey}" introuvable dans votre catalogue.`);
        return;
      }

      // Chantier 2.2 (2026-05-15) — Si la modale a fourni un realCallSessionId
      // (= on vient d'un débrief Sales Coach Réel), on déclenche la création
      // d'un CAS PRIVÉ persistant en BDD via coaching-brief IA. Best-effort :
      // si l'appel échoue, le flow continue (l'user a l'ancien comportement
      // localStorage). En cas de succès, on stocke aussi targetId pour usage
      // futur (bibliothèque privée + pré-sélection TrainingPage).
      try {
        const raw = localStorage.getItem("bizcoach_pending_coaching_context");
        if (raw) {
          const ctx = JSON.parse(raw) as {
            offerId?: string;
            personaId?: string;
            focus?: string;
            difficulty?: "easy" | "medium" | "hard";
            // Détails B2B Sales (aligné Valérie commit d0197ad) — passés au
            // backend pour insérer un cas privé correctement typé (industry +
            // companySize au lieu du hardcode caseType: "B2B" / null/null).
            industry?: string[];
            companySize?: string;
            customIndustry?: string;
            // Contexte spécifique au simulateur (cf table contexts) — propagé
            // au backend pour stocker contextId sur le target privé, et donc
            // descendre dans le payload N8N via les 4 variables context* (cf
            // training.ts L678+ : contextName/Description/PlayerBrief/AgentInstructions).
            contextId?: string | null;
            realCallSessionId?: string;
          };
          if (ctx.realCallSessionId && ctx.offerId && ctx.personaId && ctx.focus) {
            const { salesCoachApi } = await import("./lib/api");
            const brief = await salesCoachApi.createCoachingBrief(ctx.realCallSessionId, {
              simulatorId: agent.id,
              offerId: ctx.offerId,
              personaId: ctx.personaId,
              focusRaw: ctx.focus,
              difficulty: ctx.difficulty,
              industry: ctx.industry,
              companySize: ctx.companySize,
              customIndustry: ctx.customIndustry,
              contextId: ctx.contextId ?? undefined,
            }).catch((err) => {
              console.warn("[sales-coach] createCoachingBrief best-effort failed:", err);
              return null;
            });
            if (brief) {
              // Enrichit le contexte localStorage avec le targetId créé + scenarioInstruction
              // (qui remplace le focus brut côté N8N pour un meilleur scénario).
              localStorage.setItem("bizcoach_pending_coaching_context", JSON.stringify({
                ...ctx,
                privateTargetId: brief.targetId,
                focus: brief.scenarioInstruction,  // override avec la version IA-enrichie
                difficulty: brief.suggestedDifficulty,
              }));
            }
          }
        }
      } catch (err) {
        console.warn("[sales-coach] coaching-brief preprocessing failed:", err);
      }

      // Bypass AgentPresentationPage : le user a déjà tout configuré dans le
      // popup PrepareTrainingFromCoachDialog (offre + persona + focus + difficulté).
      // L'écran de présentation entre les deux est une friction inutile
      // (objectif Fabien : scénario généré directement après le clic popup).
      handleSelectAgent({ ...agent, isUnlocked: agent.isUnlocked ?? true }, undefined, { bypassPresentation: true });
    } catch (err) {
      console.error("[sales-coach] handleStartTrainingFromCoach:", err);
      alert("Impossible de lancer le simulateur. Réessayez.");
    }
  };

  const handleSelectAgent = async (agent: AIAgent, challengeId?: string, options?: { bypassPresentation?: boolean }) => {
    // If a training session is active, redirect to it instead
    if (trainingActive) {
      setCurrentPage("training");
      return;
    }
    setChallengeContextId(challengeId || null);
    // Bypass de l'écran AgentPresentationPage si :
    //  - on vient d'un challenge (l'agent a déjà été choisi explicitement)
    //  - on vient d'un débrief Coach Debrief (options.bypassPresentation, cf
    //    handleStartTrainingFromCoach) : le user a déjà tout configuré dans
    //    le popup PrepareTrainingFromCoachDialog, l'écran intermédiaire est
    //    une friction inutile (objectif Fabien : scénario direct).
    const bypassPresentation = !!challengeId || !!options?.bypassPresentation;
    // If agent is missing presentationJson (e.g. launched from challenge), fetch full data
    if (!agent.presentationJson) {
      try {
        const agents = await authApi.getAgents();
        const fullAgent = agents.find((a: AIAgent) => a.id === agent.id);
        if (fullAgent) {
          setSelectedAgent({ ...fullAgent, isUnlocked: fullAgent.isUnlocked !== undefined ? fullAgent.isUnlocked : true });
          setCurrentPage(bypassPresentation ? "training" : "agentPresentation");
          return;
        }
      } catch {
        // fallback to minimal agent
      }
    }
    setSelectedAgent(agent);
    setCurrentPage(bypassPresentation ? "training" : "agentPresentation");
  };

  // Étape 8 — lancer une passe de duel : résout le simulateur, mémorise le contexte
  // duel (lu par TrainingPage → rattache la session au duel + cadre « DUEL »), et
  // va droit à l'entraînement.
  const handlePlayDuel = async (duel: { id: string; simulatorId: string | null; targetId: string | null; difficulty: string; opponentName?: string; stakeCoins?: number }) => {
    if (!duel.simulatorId) { alert("Simulateur du duel introuvable."); return; }
    try {
      const agents = await authApi.getAgents();
      const agent = agents.find((a: AIAgent) => a.id === duel.simulatorId);
      if (!agent) { alert("Simulateur du duel introuvable dans votre catalogue."); return; }
      localStorage.setItem("bizcoach_pending_duel", JSON.stringify({
        duelId: duel.id, simulatorId: duel.simulatorId, targetId: duel.targetId,
        difficulty: duel.difficulty, opponentName: duel.opponentName ?? "", stakeCoins: duel.stakeCoins ?? 0,
        createdAt: Date.now(),
      }));
      await handleSelectAgent({ ...agent, isUnlocked: agent.isUnlocked ?? true }, undefined, { bypassPresentation: true });
    } catch (err) {
      alert("Impossible de lancer le duel : " + (err as Error).message);
    }
  };

  const handleStartCoach = () => {
    setCurrentPage("coach");
  };

  // Bypass écran agentPresentation pour Coach Prepa : la page /coach a
  // déjà son propre formulaire d'entrée, l'écran intermédiaire de
  // présentation est une friction inutile pour cet agent.
  // Cohérent avec le court-circuit Coach Debrief (qui va direct à
  // /sales-coach/new). Voir DashboardPage.tsx pour le call-site.
  const handleSelectCoachDirect = async (agent: AIAgent) => {
    try {
      const agents = await authApi.getAgents();
      const fullAgent = agents.find((a: AIAgent) => a.id === agent.id);
      if (fullAgent) {
        setSelectedAgent({ ...fullAgent, isUnlocked: fullAgent.isUnlocked !== undefined ? fullAgent.isUnlocked : true });
      } else {
        setSelectedAgent(agent);
      }
    } catch {
      setSelectedAgent(agent);
    }
    setCurrentPage("coach");
  };

  // Lance le Coach Prepa (nouvelle fiche si sessionId absent, reprise sinon).
  // Utilisé par la page « Sessions live » (onglet Préparations).
  const launchPrepMeeting = async (sessionId?: string) => {
    try {
      const agents = await authApi.getAgents();
      const prepAgent = agents.find((a: AIAgent) => detectCoachKind(a.name, a.simulatorType) === "prepa");
      if (!prepAgent) { alert("Coach Prepa introuvable dans votre catalogue."); return; }
      setPrepMeetingResumeSessionId(sessionId ?? null);
      setSelectedAgent({ ...prepAgent, isUnlocked: prepAgent.isUnlocked ?? true });
      setCurrentPage("coach");
    } catch (err) {
      alert("Impossible d'ouvrir la préparation : " + (err as Error).message);
    }
  };

  // Couture Prep Meeting → Training (extrait pour être réutilisé par la page Terrain
  // où le formulaire de prépa est embarqué inline).
  const handleCoachStartTraining = (payload: any, simulator: AIAgent) => {
    try {
      localStorage.setItem("bizcoach_pending_coaching_context", JSON.stringify({
        offerId: payload.offerId,
        personaId: payload.personaId,
        focus: payload.scenarioInstruction,
        difficulty: payload.suggestedDifficulty,
        privateTargetId: payload.targetId,
        createdAt: Date.now(),
      }));
    } catch (err) {
      console.warn("[prep→training] localStorage write failed:", err);
    }
    handleSelectAgent({ ...simulator, isUnlocked: simulator.isUnlocked ?? true }, undefined, { bypassPresentation: true });
  };

  const handleCoachComplete = () => {
    setSelectedAgent(null);
    setPrepMeetingResumeSessionId(null);
    const back = coachBackPage ?? "dashboard";
    setCoachBackPage(null);
    setCurrentPage(back);
  };

  const handleLeaveCoach = () => {
    setSelectedAgent(null);
    setPrepMeetingResumeSessionId(null);
    const back = coachBackPage ?? "dashboard";
    setCoachBackPage(null);
    setCurrentPage(back);
  };

  const handleStartTraining = () => {
    setCurrentPage("training");
  };

  const handleTrainingComplete = (_feedback: Feedback) => {
    setTrainingActive(false);
    setSelectedAgent(null);
    notifySeancesChanged();
    // If training was from a challenge, go back to challenge page
    if (challengeContextId) {
      setSelectedChallengeId(challengeContextId);
      setChallengeContextId(null);
      setCurrentPage("challenge");
    } else {
      setCurrentPage("dashboard");
    }
  };

  const handleLeaveTraining = () => {
    setTrainingActive(false);
    setSelectedAgent(null);
    if (challengeContextId) {
      setSelectedChallengeId(challengeContextId);
      setChallengeContextId(null);
      setCurrentPage("challenge");
    } else {
      setCurrentPage("dashboard");
    }
  };

  const handleNavigateToChallenge = (id: string) => {
    setChallengeBackPage(currentPage);
    setSelectedChallengeId(id);
    setCurrentPage("challenge");
  };

  const handleUpdateUser = (updates: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...updates });
    }
  };

  // Fetch active challenges for banner
  const refreshActiveChallenges = () => {
    if (isAuthenticated && user) {
      challengesApi.getMyChallenges()
        .then(setActiveChallenges)
        .catch(() => setActiveChallenges([]));
    }
  };
  useEffect(() => {
    refreshActiveChallenges();
  }, [isAuthenticated, user, currentPage]);

  // Rediriger vers dashboard si un user non-admin accède à /admin
  useEffect(() => {
    if (user && (currentPage === "admin" || currentPage === "admin-account") && user.profileType !== "ADM" && user.profileType !== "COO") {
      setCurrentPage("dashboard");
    }
    if (user && currentPage === "admin-coaching-briefs" && !["ADM", "COO", "ORG"].includes(user.profileType ?? "")) {
      setCurrentPage("dashboard");
    }
    if (user && currentPage === "admin-eval-criteria-matrix" && user.profileType !== "ADM") {
      setCurrentPage("dashboard");
    }
    if (user && currentPage === "admin-personality-styles" && user.profileType !== "ADM") {
      setCurrentPage("dashboard");
    }
    // Banque de cas : ADM et COO (contenu pédagogique Kestio, pas de donnée client).
    if (user && currentPage === "admin-banque-de-cas" && !["ADM", "COO"].includes(user.profileType)) {
      setCurrentPage("dashboard");
      return;
    }
    if (user && currentPage === "admin-drill" && user.profileType !== "ADM") {
      setCurrentPage("dashboard");
    }
    if (user && currentPage === "admin-kestio-knowledge" && user.profileType !== "ADM") {
      setCurrentPage("dashboard");
    }
    if (user && currentPage === "admin-llm-routes" && user.profileType !== "ADM") {
      setCurrentPage("dashboard");
    }
    if (user && currentPage === "admin-relance-rules" && user.profileType !== "ADM") {
      setCurrentPage("dashboard");
    }
    if (user && currentPage === "admin-reusability-kpis" && user.profileType !== "ADM" && user.profileType !== "COO") {
      setCurrentPage("dashboard");
    }
    if (user && currentPage === "admin-path-templates" && user.profileType !== "ADM") {
      setCurrentPage("dashboard");
    }
    if (user && currentPage === "admin-program-rules" && user.profileType !== "ADM") {
      setCurrentPage("dashboard");
    }
    if (user && currentPage === "admin-intent-contexts" && user.profileType !== "ADM") {
      setCurrentPage("dashboard");
    }
    if (user && currentPage === "admin-portfolios" && user.profileType !== "ADM") {
      setCurrentPage("dashboard");
    }
    if (user && currentPage === "admin-legal-documents" && user.profileType !== "ADM") {
      setCurrentPage("dashboard");
    }
  }, [currentPage, user]);

  // Écran de chargement pendant l'auto-login
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="text-white text-lg">{t("common.loading")}</div>
      </div>
    );
  }

  // Signup pages (public, no auth required)
  if (window.location.pathname.startsWith("/signup")) {
    return <SignupPage />;
  }

  // Galerie de styles de waveform — outil de design interne, réservé au dev
  // (localhost / *.osc-fr1.scalingo.io). Inaccessible sur staging/demo/prod.
  if (isDevHost() && window.location.pathname.startsWith("/waveforms")) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><span className="text-sm" style={{ color: "#6B7280" }}>Chargement…</span></div>}>
        <WaveformGalleryPage />
      </Suspense>
    );
  }

  // Page publique de vérification d'un certificat (pas d'auth)
  if (window.location.pathname.startsWith("/certificate/")) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><span className="text-sm" style={{ color: "#6B7280" }}>Chargement…</span></div>}>
        <CertificatePage />
      </Suspense>
    );
  }

  // V3 — Page publique de consultation d'un clip partagé (pas d'auth)
  if (window.location.pathname.startsWith("/sales-coach/share/")) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><span className="text-sm" style={{ color: "#6B7280" }}>Chargement…</span></div>}>
        <SalesCoachSharePage />
      </Suspense>
    );
  }

  // Magic link verification page
  if (magicToken && !isAuthenticated) {
    return (
      <AuthVerifyPage
        token={magicToken}
        onLogin={handleLogin}
        onBack={() => {
          setMagicToken(null);
          window.history.replaceState({}, "", "/");
        }}
      />
    );
  }

  // Non authentifié -> page d'auth (magic link)
  if (!isAuthenticated || !user) {
    return <AuthPage onLogin={handleLogin} />;
  }

  // DPA à accepter par l'ORG : avant tout le reste, onboarding compris. Tant que la vérification
  // n'a pas répondu, rien ne s'affiche (pas d'application entrevue puis retirée).
  if (user.profileType === "ORG" && legalPending === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#384872]" />
      </div>
    );
  }
  if (legalPending) {
    return (
      <LegalAcceptanceGate
        pending={legalPending}
        onAccepted={() => setLegalPending(null)}
        onReload={checkLegalPending}
        onLogout={handleLogout}
      />
    );
  }

  // Onboarding for new ORG users
  if (showOnboarding) {
    return (
      <OnboardingPage
        user={user}
        onComplete={markOnboardingDone}
        onSkip={markOnboardingDone}
      />
    );
  }

  /**
   * @param target sous-cible facultative pour l'espace encadrant : permet
   *   d'ouvrir un bloc ET son sous-écran d'un seul geste (ex. depuis une
   *   alerte du cockpit vers « Concevoir › Parcours »).
   */
  const handleNavigate = (page: PageType, target?: { bloc?: EncadrantBloc; sub?: string; tab?: string }) => {
    if (page === "challenge") {
      setSelectedChallengeId(null);
    }
    if (target?.bloc) setEncadrantBloc(target.bloc);
    setAccountTab(target?.tab);
    // `sub` est réinitialisé même sans cible : sinon un sous-écran d'un
    // précédent passage resterait sélectionné dans le nouveau bloc.
    setEncadrantSub(target?.sub);
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  /** Changement de bloc depuis la barre du haut : on repart du 1er sous-écran. */
  const handleSetEncadrantBloc = (bloc: EncadrantBloc) => {
    setEncadrantBloc(bloc);
    setEncadrantSub(undefined);
  };

  // Unified render — TrainingPage stays mounted (hidden) when session is active
  return (
    <>
    <Layout
      user={user}
      currentPage={currentPage}
      onNavigate={handleNavigate}
      onLogout={handleLogout}
      activeTraining={trainingActive && selectedAgent ? {
        agentName: selectedAgent.name,
        agentIcon: selectedAgent.icon,
        agentImage: selectedAgent.image,
        agentColor: selectedAgent.color,
        onReturn: () => setCurrentPage("training"),
      } : undefined}
      activeChallenges={activeChallenges}
      onNavigateToChallenge={handleNavigateToChallenge}
      onChallengeJoined={refreshActiveChallenges}
      encadrantBloc={encadrantBloc}
      onSetEncadrantBloc={handleSetEncadrantBloc}
    >
      {/* TrainingPage — stays mounted while session is active, hidden when not on training page */}
      {selectedAgent && (currentPage === "training" || trainingActive) && (
        <div style={{ display: currentPage === "training" ? undefined : "none" }}>
          <TrainingPage
            agent={selectedAgent}
            challengeId={challengeContextId || undefined}
            onBack={handleLeaveTraining}
            onComplete={handleTrainingComplete}
            onSessionActive={(active) => setTrainingActive(active)}
            onNavigate={handleNavigate}
          />
        </div>
      )}

      <Suspense fallback={<div className="flex items-center justify-center py-20 text-muted-foreground text-sm">Chargement...</div>}>
      {/* AgentPresentation — only when no active training session */}
      {currentPage === "agentPresentation" && selectedAgent && !trainingActive && (
        <AgentPresentationPage
          agent={selectedAgent}
          onBack={() => {
            setSelectedAgent(null);
            setCurrentPage("dashboard");
          }}
          onStartTraining={selectedAgent.formSchema ? handleStartCoach : handleStartTraining}
        />
      )}

      {/* CoachPage — separate page for coach with form */}
      {currentPage === "coach" && selectedAgent && (
        <CoachPage
          agent={selectedAgent}
          onBack={handleLeaveCoach}
          onComplete={handleCoachComplete}
          userProfileType={user.profileType}
          existingSessionId={prepMeetingResumeSessionId}
          onStartTraining={handleCoachStartTraining}
        />
      )}

      {/* Other pages — shown when not on training/presentation/coach page */}
      {currentPage !== "training" && currentPage !== "agentPresentation" && currentPage !== "coach" && (
        <>
          {currentPage === "dashboard" && (
            <DashboardPage
              user={user}
              onStartTraining={handleSelectAgent}
              onStartCoachDirect={handleSelectCoachDirect}
              onViewHistory={(sessionId?: string) => {
                setSelectedSessionId(sessionId || null);
                setCurrentPage("history");
              }}
              onNavigate={handleNavigate}
              onNavigateToChallenge={handleNavigateToChallenge}
              onOpenRecord={() => setRecordModalOpen(true)}
            />
          )}
          {recordModalOpen && (
            <React.Suspense fallback={null}>
              <RecordMeetingModal
                onClose={() => setRecordModalOpen(false)}
                onCreated={(id) => { setRecordModalOpen(false); setSalesCoachSessionId(id); setCurrentPage("sales-coach-detail"); }}
              />
            </React.Suspense>
          )}
          {currentPage === "history" && (
            <HistoryPage
              user={user}
              onBack={() => { setSelectedSessionId(null); setCurrentPage("dashboard"); }}
              initialSessionId={selectedSessionId}
              initialHistoryTab={historyInitialTab ?? undefined}
              onNavigateToSalesCoach={() => setCurrentPage("sessions-live")}
              onNavigate={(page) => setCurrentPage(page)}
              onOpenChallenge={handleNavigateToChallenge}
              onOpenTeamHistory={() => { openTeamHistory(); handleNavigate("encadrant", { bloc: "piloter", sub: "activite" }); }}
              onOpenPrepMeeting={async (sessionId) => {
                // Reprise d'une fiche / brouillon Prep Meeting (2026-05-16) :
                // charge l'agent "Prep Meeting" + stocke le sessionId + navigue vers /coach
                try {
                  const agents = await authApi.getAgents();
                  // Détection centralisée + tolérante au renommage du `name` (cf. lib/coach-kind).
                  const prepAgent = agents.find((a: AIAgent) => detectCoachKind(a.name, a.simulatorType) === "prepa");
                  if (!prepAgent) {
                    alert("Coach Prepa introuvable dans votre catalogue.");
                    return;
                  }
                  setPrepMeetingResumeSessionId(sessionId);
                  // Mémorise l'origine pour que « Retour » depuis CoachPage renvoie ici
                  // (au lieu du dashboard) — cf. Mariam 01/07.
                  setCoachBackPage("history");
                  setHistoryInitialTab("prep-meeting");
                  setSelectedAgent(prepAgent);
                  setCurrentPage("coach");
                } catch (err) {
                  alert("Impossible d'ouvrir la fiche : " + (err as Error).message);
                }
              }}
            />
          )}
          {currentPage === "challenge" && selectedChallengeId && (
            <ChallengePage
              user={user}
              challengeId={selectedChallengeId}
              onStartTraining={(agent, chalId) => handleSelectAgent(agent, chalId)}
              onBack={() => {
                setSelectedChallengeId(null);
                setCurrentPage(challengeBackPage);
              }}
              onOpenTeamHistory={() => { openTeamHistory(); handleNavigate("encadrant", { bloc: "piloter", sub: "activite" }); }}
            />
          )}
          {currentPage === "challenge" && !selectedChallengeId && (
            <ChallengePage
              user={user}
              onStartTraining={(agent, chalId) => handleSelectAgent(agent, chalId)}
              onBack={() => setCurrentPage("dashboard")}
              onSelectChallenge={handleNavigateToChallenge}
              onPlayDuel={handlePlayDuel}
              onOpenTeamHistory={() => { openTeamHistory(); handleNavigate("encadrant", { bloc: "piloter", sub: "activite" }); }}
            />
          )}
          {/* « Mon équipe » : gardée par profil (la page s'ouvrait par son adresse, GES compris). */}
          {currentPage === "reporting" && !canOpenTeamReporting(user.profileType) && (
            <div className="max-w-2xl mx-auto py-12 px-4 text-center text-sm text-muted-foreground" role="alert">{t("common.noAccess")}</div>
          )}
          {currentPage === "reporting" && canOpenTeamReporting(user.profileType) && (
            <div className="max-w-6xl mx-auto py-6 px-4">
              <h1 className="text-2xl font-bold mb-6" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{t("nav.myTeam")}</h1>
              <ReportingTab userProfileType={user.profileType} />
            </div>
          )}
          {currentPage === "encadrant" && (() => {
            const role = encadrantRoleFor(user.profileType);
            return role ? (
              <EncadrantPage user={user} role={role} bloc={encadrantBloc} sub={encadrantSub} onSubChange={setEncadrantSub} onNavigate={handleNavigate} onNavigateToChallenge={handleNavigateToChallenge} />
            ) : null;
          })()}
          {currentPage === "m-entrainer" && (
            <MEntrainerPage
              user={user}
              onNavigate={(page) => setCurrentPage(page as PageType)}
              onStartTraining={(agent) => handleSelectAgent(agent)}
              onNavigateToChallenge={handleNavigateToChallenge}
              onOpenTeamHistory={() => { openTeamHistory(); handleNavigate("encadrant", { bloc: "piloter", sub: "activite" }); }}
            />
          )}
          {currentPage === "rewards" && (
            <RewardsPage />
          )}
          {currentPage === "certification-paths" && (
            <CertificationPathsPage
              user={user}
              mode="learner"
              onLaunchPhase={(agent, ctx) => {
                try {
                  localStorage.setItem("bizcoach_pending_path_phase", JSON.stringify({ ...ctx, createdAt: Date.now() }));
                } catch { /* noop */ }
                handleSelectAgent({ ...agent, isUnlocked: agent.isUnlocked ?? true }, undefined, { bypassPresentation: true });
              }}
              onOpenTeamHistory={() => { openTeamHistory(); handleNavigate("encadrant", { bloc: "piloter", sub: "activite" }); }}
            />
          )}
          {currentPage === "my-progress" && (
            <MyProgressPage onNavigate={handleNavigate} />
          )}
          {currentPage === "account" && (
            <AccountPage user={user} onUpdate={handleUpdateUser} onNavigateToChallenge={handleNavigateToChallenge} initialTab={accountTab} onOpenCompanyNotifications={() => setCurrentPage("company-notification-settings")} />
          )}
          {currentPage === "profile" && (
            <AccountPage user={user} onUpdate={handleUpdateUser} onNavigateToChallenge={handleNavigateToChallenge} variant="profile" />
          )}
          {currentPage === "sessions-training" && (
            <SessionsTrainingPage
              user={user}
              onStartTraining={(agent) => handleSelectAgent(agent)}
              onNavigate={(page) => setCurrentPage(page as PageType)}
              onOpenChallenge={handleNavigateToChallenge}
              onOpenPrepMeeting={(sessionId) => launchPrepMeeting(sessionId)}
              onOpenTeamHistory={() => { openTeamHistory(); handleNavigate("encadrant", { bloc: "piloter", sub: "activite" }); }}
              initialSessionId={selectedSessionId}
            />
          )}
          {currentPage === "sessions-live" && (
            <SessionsLivePage
              user={user}
              onCreateNew={() => setCurrentPage("sales-coach-new")}
              onOpenDetail={(id) => { setSalesCoachSessionId(id); setCurrentPage("sales-coach-detail"); }}
              onStartTrainingForKey={handleStartTrainingFromCoach}
              onOpenLibrary={() => setCurrentPage("sales-coach-library")}
              onOpenPrep={(sessionId) => launchPrepMeeting(sessionId)}
              onCoachStartTraining={handleCoachStartTraining}
            />
          )}
          {currentPage === "help" && (
            <HelpPage user={user} onNavigate={handleNavigate} />
          )}
          {currentPage === "buy-pack" && (
            <BuyPackPage onNavigate={setCurrentPage} />
          )}
          {currentPage === "preview-rate-limit" && (
            <PreviewRateLimitPage onNavigate={setCurrentPage} user={user} />
          )}
          {currentPage === "cost-dashboard" && (
            <CostDashboardPage onNavigate={setCurrentPage} user={user} />
          )}
          {currentPage === "usage-analytics" && (
            <UsageAnalyticsPage onNavigate={setCurrentPage} user={user} />
          )}
          {currentPage === "admin-tech-doc" && (
            <AdminTechDocPage onNavigate={setCurrentPage} user={user} />
          )}
          {currentPage === "notifications" && (
            <NotificationsPage
              onBack={() => setCurrentPage("dashboard")}
              onOpenPreferences={() => setCurrentPage("notification-preferences")}
            />
          )}
          {currentPage === "notification-preferences" && (
            <NotificationPreferencesPage onBack={() => setCurrentPage("account")} />
          )}
          {currentPage === "company-notification-settings" && user.clientAccountId && (
            <CompanyNotificationSettingsPage
              companyId={user.clientAccountId}
              onBack={() => setCurrentPage("account")}
            />
          )}
          {currentPage === "admin" && (user.profileType === "ADM" || user.profileType === "COO") && (
            <AdminPage
              onViewAccount={(accountId) => {
                setAdminTargetAccountId(accountId);
                setCurrentPage("admin-account");
              }}
              onNavigate={setCurrentPage}
              userProfile={user.profileType ?? undefined}
            />
          )}
          {currentPage === "admin-coaching-briefs" && (user.profileType === "ADM" || user.profileType === "COO" || user.profileType === "ORG") && (
            <AdminCoachingBriefsPage
              onBack={() => setCurrentPage(user.profileType === "ORG" ? "account" : "admin")}
            />
          )}
          {currentPage === "admin-eval-criteria-matrix" && user.profileType === "ADM" && (
            <AdminEvaluationCriteriaMatrixPage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "admin-personality-styles" && user.profileType === "ADM" && (
            <AdminPersonalityStylesPage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "admin-banque-de-cas" && ["ADM", "COO"].includes(user.profileType) && (
            <AdminBanqueDeCasPage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "admin-drill" && user.profileType === "ADM" && (
            <AdminDrillPage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "drill" && drillVisible && (
            <DrillPage onBack={() => { try { localStorage.removeItem("bizcoach_pending_drill"); } catch {} setCurrentPage("history"); }} />
          )}
          {currentPage === "admin-kestio-knowledge" && user.profileType === "ADM" && (
            <AdminKestioKnowledgePage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "admin-llm-routes" && user.profileType === "ADM" && (
            <AdminLLMRoutesPage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "admin-relance-rules" && user.profileType === "ADM" && (
            <AdminRelanceRulesPage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "admin-reusability-kpis" && (user.profileType === "ADM" || user.profileType === "COO") && (
            <AdminReusabilityKpisPage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "admin-path-templates" && user.profileType === "ADM" && (
            <AdminPathTemplatesPage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "admin-program-rules" && user.profileType === "ADM" && (
            <AdminProgramRulesPage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "admin-intent-contexts" && user.profileType === "ADM" && (
            <AdminIntentContextsPage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "admin-portfolios" && user.profileType === "ADM" && (
            <AdminPortfoliosPage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "admin-legal-documents" && user.profileType === "ADM" && (
            <AdminLegalDocumentsPage onBack={() => setCurrentPage("admin")} />
          )}
          {currentPage === "admin-account" && adminTargetAccountId && (user.profileType === "ADM" || user.profileType === "COO") && (
            <AccountPage
              user={user}
              onUpdate={handleUpdateUser}
              adminAccountId={adminTargetAccountId}
              onBackToAdmin={() => {
                setAdminTargetAccountId(null);
                setCurrentPage("admin");
              }}
            />
          )}
          {/* Sales Coach IA Réel — point d'entrée unique = Terrain (sessions-live).
              L'écran liste autonome « sales-coach » et l'orphelin « sales-coach-team »
              ont été retirés (dédup 17/07) ; ces pages studio restent accessibles
              depuis Terrain, retour → Terrain. */}
          {currentPage === "sales-coach-new" && (
            <SalesCoachNewPage
              onBack={() => setCurrentPage("sessions-live")}
              onCreated={(id) => {
                // Creation validee : on va sur la fiche detail SANS reset salesCoachBackPage,
                // pour que le retour depuis la fiche revienne aussi a l'origine.
                setSalesCoachSessionId(id);
                setCurrentPage("sales-coach-detail");
              }}
            />
          )}
          {currentPage === "sales-coach-detail" && salesCoachSessionId && (
            <SalesCoachDetailPage
              sessionId={salesCoachSessionId}
              onBack={() => {
                setSalesCoachSessionId(null);
                setCurrentPage("sessions-live");
              }}
              onStartTrainingForKey={handleStartTrainingFromCoach}
              onPrepareMeeting={() => launchPrepMeeting()}
              messageRegister={user?.companyMessageRegister}
            />
          )}
          {currentPage === "sales-coach-library" && (
            <SalesCoachLibraryPage
              onBack={() => setCurrentPage("sessions-live")}
              onStartTrainingForKey={handleStartTrainingFromCoach}
            />
          )}
        </>
      )}
      </Suspense>
    </Layout>
    </>
  );
}
